/**
 * Broker connection helpers for TradeStation Direct vs Connect Trade.
 */

import {
  CONNECT_TRADE_BROKERS,
  MARKET_DATA,
  findConnectTradeBroker,
  getBrokerDisplayNameMap,
} from '../data/shared/connectTradeBrokers';
import { getBrokerAssetClass } from './tickerUtils';

/** Connect Trade brokers that expose live bars/quotes. */
const MD_CAPABLE_CT_BROKERS = new Set(
  (CONNECT_TRADE_BROKERS || [])
    .filter((b) => b.marketData === MARKET_DATA.YES)
    .flatMap((b) => [String(b.ctCode || '').toUpperCase(), String(b.id).toUpperCase().replace(/-/g, '_')])
    .filter(Boolean)
);

/**
 * Connect Trade brokers known to support live order placement.
 * Keys match server `broker_name` / CONNECTTRADE_BROKERS in brokerCapabilities.js.
 * Unknown CT brokers default to no live trading (account-data aggregation).
 */
const CT_LIVE_TRADING_BROKERS = new Set(
  (CONNECT_TRADE_BROKERS || [])
    .filter((b) => b.liveTrading === true && b.ptSupported !== false)
    .flatMap((b) => [String(b.ctCode || '').toUpperCase(), String(b.id).toUpperCase().replace(/-/g, '_')])
    .filter(Boolean)
);

const BROKER_DISPLAY_NAMES = getBrokerDisplayNameMap();

/** Human-readable broker label for sidebar / connection status. */
export function formatBrokerDisplayName(brokerName) {
  if (!brokerName) return 'Broker';
  const key = String(brokerName).toUpperCase();
  if (BROKER_DISPLAY_NAMES[key]) return BROKER_DISPLAY_NAMES[key];
  return key
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

/** e.g. "Webull · Connect Trade" */
export function formatBrokerConnectionLabel(conn) {
  if (!conn) return null;
  const broker = formatBrokerDisplayName(conn.broker_name);
  const via = conn.adapter_type === 'connecttrade' ? 'Connect Trade' : 'Direct';
  return `${broker} · ${via}`;
}

export function isTradestationDirectBroker(user) {
  if (!user) return false;
  const list = user.brokerConnections || [];
  if (list.some((c) => c.adapter_type === 'tradestation_direct')) return true;
  const active = user.activeBrokerConnection;
  if (active?.adapter_type === 'tradestation_direct') return true;
  // Legacy Direct (api_credentials) may coexist with CT non-TS brokers.
  return user.hasTradeStationCredentials === true;
}

export function isConnectTradeBroker(user) {
  if (!user) return false;
  const list = user.brokerConnections || [];
  if (list.some((c) => c.adapter_type === 'connecttrade')) return true;
  const active = user.activeBrokerConnection;
  if (active?.adapter_type === 'connecttrade') return true;
  return user.hasBrokerConnection === true && user.hasTradeStationCredentials !== true;
}

/** True when the selected account is on Connect Trade (not Direct). Prefer this over user-global. */
export function isConnectTradeAccount(account) {
  if (!account) return false;
  if (account.adapter_type === 'tradestation_direct') return false;
  if (account.adapter_type === 'connecttrade') return true;
  return Boolean(account.ct_connection_id || account.connection_id);
}

/** True when the selected account is Charles Schwab via Connect Trade. */
export function isConnectTradeSchwabAccount(account) {
  if (!isConnectTradeAccount(account)) return false;
  const key = normalizeBrokerKey(account.broker_name || account.institution_name);
  return key.includes('SCHWAB');
}

/** True when user already has TradeStation linked via Connect Trade. */
export function hasConnectTradeTradestation(user) {
  if (!user) return false;
  const list = user.brokerConnections || [];
  return list.some(
    (c) =>
      c.adapter_type === 'connecttrade' &&
      String(c.broker_name || '')
        .toUpperCase()
        .includes('TRADESTATION')
  );
}

/** True when TradeStation Direct is linked (blocks CT TradeStation only). */
export function hasTradestationDirect(user) {
  return isTradestationDirectBroker(user);
}

export function isBrokerLinked(user) {
  if (!user) return false;
  return user.hasBrokerConnection === true || user.hasTradeStationCredentials === true;
}

function normalizeBrokerKey(name) {
  return String(name || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function connectionSupportsMarketData(conn) {
  if (!conn) return false;
  if (conn.adapter_type === 'tradestation_direct') return true;
  if (conn.adapter_type && conn.adapter_type !== 'connecttrade') return false;
  const key = normalizeBrokerKey(conn.broker_name);
  if (!key) return false;
  if (MD_CAPABLE_CT_BROKERS.has(key)) return true;
  for (const code of MD_CAPABLE_CT_BROKERS) {
    const normalized = normalizeBrokerKey(code);
    if (normalized.length >= 4 && key.includes(normalized)) return true;
  }
  return false;
}

/**
 * True when the account carries enough identity to map to a broker_connections row.
 * Bootstrap selections are often `{ AccountID, isPaperTrading }` only — those must not
 * inherit a stale activeBrokerConnection (e.g. Fidelity) for MD / Trade access checks.
 */
export function hasResolvableBrokerIdentity(account) {
  if (!account) return false;
  return Boolean(
    account.adapter_type ||
      account.ct_connection_id ||
      account.connection_id ||
      account.broker_name ||
      account.institution_name
  );
}

/**
 * Map a brokerage account to its broker_connections row (or Direct synthetic).
 * Used when switching accounts across multi-broker links so MD/trade nav follows
 * the selected account rather than stale activeBrokerConnection (connections[0]).
 */
export function resolveBrokerConnectionForAccount(user, account) {
  if (!user) return null;
  if (!account) return user.activeBrokerConnection || null;

  const connections = Array.isArray(user.brokerConnections) ? user.brokerConnections : [];

  if (account.adapter_type === 'tradestation_direct') {
    return (
      connections.find((c) => c.adapter_type === 'tradestation_direct') || {
        adapter_type: 'tradestation_direct',
        broker_name: 'TRADESTATION',
        connection_id: null,
        status: 'active',
      }
    );
  }

  const connId = account.ct_connection_id || account.connection_id;
  if (connId) {
    const byId = connections.find((c) => String(c.connection_id) === String(connId));
    if (byId) return byId;
  }

  const brokerKey = String(account.broker_name || account.institution_name || '').toUpperCase();
  if (brokerKey) {
    const byName = connections.find(
      (c) => String(c.broker_name || '').toUpperCase() === brokerKey
    );
    if (byName) return byName;
  }

  // Bootstrap AccountID-only objects: do not guess via activeBrokerConnection.
  if (account.AccountID && !hasResolvableBrokerIdentity(account)) {
    return null;
  }

  // The account names its broker, but that row is not in brokerConnections yet
  // (or the id didn't match). Keep the account's own institution — do not
  // substitute a sibling link such as Fidelity. Effective MD is resolved separately.
  if (hasResolvableBrokerIdentity(account)) {
    return {
      adapter_type: account.adapter_type || (connId ? 'connecttrade' : null),
      broker_name: account.broker_name || account.institution_name || null,
      connection_id: connId || null,
      status: 'active',
    };
  }

  return user.activeBrokerConnection || connections[0] || null;
}

function connectionIdentityKey(conn) {
  if (!conn) return '';
  return [
    conn.adapter_type || '',
    conn.connection_id || '',
    String(conn.broker_name || '').toUpperCase(),
  ].join('|');
}

/** True when two connection refs point at the same broker link. */
export function brokerConnectionsEqual(a, b) {
  return connectionIdentityKey(a) === connectionIdentityKey(b);
}

function isUnresolvedAccount(account) {
  return Boolean(account?.AccountID && !hasResolvableBrokerIdentity(account));
}

function listUserConnections(user) {
  const connections = Array.isArray(user?.brokerConnections) ? [...user.brokerConnections] : [];
  if (
    isTradestationDirectBroker(user) &&
    !connections.some((c) => c.adapter_type === 'tradestation_direct')
  ) {
    connections.push({
      adapter_type: 'tradestation_direct',
      broker_name: 'TRADESTATION',
      connection_id: null,
      status: 'active',
    });
  }
  return connections;
}

function mdPreferenceRank(conn) {
  if (!conn) return 99;
  if (conn.adapter_type === 'tradestation_direct') return 0;
  if (String(conn.broker_name || '').toUpperCase().includes('TRADESTATION')) return 1;
  return 2;
}

function pickPreferredMdConnection(candidates) {
  if (!Array.isArray(candidates) || candidates.length === 0) return null;
  return [...candidates].sort((a, b) => {
    const rank = mdPreferenceRank(a) - mdPreferenceRank(b);
    if (rank !== 0) return rank;
    const idA = a.id == null ? Number.MAX_SAFE_INTEGER : Number(a.id);
    const idB = b.id == null ? Number.MAX_SAFE_INTEGER : Number(b.id);
    if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) return idA - idB;
    return 0;
  })[0];
}

/**
 * Connection that should serve quotes/bars/charts.
 * Selected account if it has MD; otherwise Direct → CT TradeStation → any other MD link.
 */
export function resolveMarketDataConnection(user, account = null) {
  if (!user) return null;
  if (account && isUnresolvedAccount(account)) return null;

  if (account) {
    const owned = resolveBrokerConnectionForAccount(user, account);
    if (connectionSupportsMarketData(owned)) return owned;
  } else if (connectionSupportsMarketData(user.activeBrokerConnection)) {
    return user.activeBrokerConnection;
  }

  const capable = listUserConnections(user).filter(connectionSupportsMarketData);
  if (capable.length > 0) return pickPreferredMdConnection(capable);
  if (isTradestationDirectBroker(user)) {
    return {
      adapter_type: 'tradestation_direct',
      broker_name: 'TRADESTATION',
      connection_id: null,
      status: 'active',
    };
  }
  return null;
}

/** Account stays selected; connection/adapter come from the effective MD source. */
export function getMarketDataRouting(user, account = null) {
  const md = resolveMarketDataConnection(user, account);
  return {
    accountId: account?.AccountID || null,
    connectionId: md?.connection_id || account?.ct_connection_id || account?.connection_id || null,
    adapterType: md?.adapter_type || account?.adapter_type || null,
  };
}

/** True only when the selected (or active) connection itself has market data. */
export function connectionOwnsMarketData(user, account = null) {
  if (!user) return false;
  if (account) {
    if (isUnresolvedAccount(account)) return false;
    return connectionSupportsMarketData(resolveBrokerConnectionForAccount(user, account));
  }
  return connectionSupportsMarketData(user.activeBrokerConnection);
}

/**
 * True when live market data is available from the selected account or a sibling
 * MD-capable connection. Bootstrap AccountID-only selections wait until hydrated.
 */
export function supportsMarketData(user, account = null) {
  if (!user) return false;
  if (account && isUnresolvedAccount(account)) return false;
  return Boolean(resolveMarketDataConnection(user, account));
}

/**
 * Connect Trade brokers whose watchlist quote subscriptions must stay off.
 * Those quotes open 1d bar subs (today + prior session) on the shared market-data
 * socket so the watchlist can show day $/% change, and that stalls chart streams.
 * Selected ticker, positions, and market-index quotes still subscribe.
 */
const WATCHLIST_QUOTES_DISABLED_BROKERS = new Set(['TASTYTRADE']);

/** False for Tastytrade until its watchlist bar-stream quotes no longer block charts. */
export function watchlistQuotesEnabled(user, account = null) {
  const md = resolveMarketDataConnection(user, account);
  if (!md || md.adapter_type === 'tradestation_direct') return true;
  return !WATCHLIST_QUOTES_DISABLED_BROKERS.has(normalizeBrokerKey(md.broker_name));
}

/**
 * Chart, watchlist, Market Overview, and Relative Strength tools.
 * Live broker bars when any linked connection has market data.
 */
export function canAccessMarketDataTools(user, account = null) {
  if (supportsMarketData(user, account)) return true;
  if (!user) return false;
  if (isUnresolvedAccount(account)) return true;
  return false;
}

/**
 * Trade workspace (charts + order entry). Requires an effective MD source once
 * a broker is linked. Unlinked users can still open Trade to connect a broker.
 *
 * Returns:
 * - true / false when decided
 * - null only when the user is linked but settings have no connection rows yet
 *   (cannot tell MD vs Portfolio). Callers should wait; otherwise paint Trade
 *   (or redirect) from /auth/settings without blocking on accounts.
 */
export function canAccessTradeWorkspace(user, account = null) {
  if (!user) return false;
  if (!isBrokerLinked(user)) return true;

  // Hydrated account: decide from that account + sibling MD links.
  if (account && hasResolvableBrokerIdentity(account)) {
    return supportsMarketData(user, account);
  }

  // No account yet / bootstrap AccountID-only: decide from settings connections
  // so Trade can paint (and no-MD users can redirect) before accounts return.
  if (supportsMarketData(user)) return true;

  const connections = listUserConnections(user);
  if (connections.length > 0) return false;

  return null;
}

/**
 * Sync check for live trading support (no capabilities fetch).
 * Used on public/marketing shells where React Query may be unavailable.
 * Unlinked users are treated as trading-capable so nav still says "Trade".
 */
export function brokerSupportsLiveTrading(user) {
  if (!user || !isBrokerLinked(user)) return true;
  if (isTradestationDirectBroker(user)) return true;
  const connections = Array.isArray(user.brokerConnections) && user.brokerConnections.length > 0
    ? user.brokerConnections
    : user.activeBrokerConnection
      ? [user.activeBrokerConnection]
      : [];
  return connections.some((conn) => {
    if (conn?.adapter_type !== 'connecttrade') return false;
    return CT_LIVE_TRADING_BROKERS.has(String(conn.broker_name || '').toUpperCase());
  });
}

/** Broker + Connect Trade trading metadata from GET /broker/capabilities (when loaded). */
export function getBrokerTradingFeatures(capabilities) {
  if (!capabilities) {
    return {
      canPlaceOrders: null,
      canUseBrackets: null,
      canUseOco: null,
      canUseOso: null,
      orderTypes: [],
      connecttrade: null,
      brokerFeatures: null,
    };
  }
  const trading = capabilities.trading || {};
  const endpoints = capabilities.endpoints || {};
  const brokerFeatures = capabilities.broker_features || null;
  return {
    canPlaceOrders: endpoints.order_create === true && trading.live_trading !== false,
    canUseBrackets: trading.supports_brackets === true && endpoints.order_group_create === true,
    canUseOco: trading.supports_oco === true,
    canUseOso: trading.supports_oso === true,
    orderTypes: trading.order_types || [],
    connecttrade: capabilities.connecttrade || null,
    brokerFeatures,
  };
}

/** True when user may submit orders (broker linked; respects capabilities when loaded). */
export function canTradeWithBroker(user, capabilities) {
  if (!isBrokerLinked(user)) return false;
  const { canPlaceOrders } = getBrokerTradingFeatures(capabilities);
  if (canPlaceOrders === null) return true;
  return canPlaceOrders;
}

/** Tooltip / banner copy when buy/sell is blocked. */
export function getTradeDisabledReason(user, capabilities) {
  if (!isBrokerLinked(user)) {
    return 'Connect broker to trade';
  }
  const { canPlaceOrders } = getBrokerTradingFeatures(capabilities);
  if (canPlaceOrders === false) {
    return 'Order placement is not available for this broker connection';
  }
  return null;
}

/** Bracket UI (templates, SL/TP, OCO) — null capabilities = show until loaded. */
export function supportsBracketTrading(capabilities) {
  const { canUseBrackets } = getBrokerTradingFeatures(capabilities);
  if (canUseBrackets === null) return true;
  return canUseBrackets;
}

/**
 * Rules that need an attached stop on entry (brackets/OCO): require_stop_loss, max_risk_per_trade.
 * Tracking still works without this; only Enforce is gated.
 * Null capabilities = allow until loaded (same pattern as supportsBracketTrading).
 */
export function supportsBracketDependentRuleEnforcement(capabilities) {
  return supportsBracketTrading(capabilities);
}

/** @deprecated Prefer supportsBracketDependentRuleEnforcement */
export function supportsRequireStopLossEnforcement(capabilities) {
  return supportsBracketDependentRuleEnforcement(capabilities);
}

export const BRACKET_DEPENDENT_RULE_ENFORCE_UNSUPPORTED_TOOLTIP =
  'Your broker does not support submitting trades with a stop-loss, so this rule cannot be enforced.';

/** @deprecated Prefer BRACKET_DEPENDENT_RULE_ENFORCE_UNSUPPORTED_TOOLTIP */
export const REQUIRE_STOP_LOSS_ENFORCE_UNSUPPORTED_TOOLTIP =
  BRACKET_DEPENDENT_RULE_ENFORCE_UNSUPPORTED_TOOLTIP;

const ALL_ASSET_CLASSES_SUPPORTED = { equities: true, options: true, futures: true };
const NO_ASSET_CLASSES_SUPPORTED = { equities: false, options: false, futures: false };

function connectionAssetClassSupport(conn) {
  if (!conn) return NO_ASSET_CLASSES_SUPPORTED;
  if (conn.adapter_type === 'tradestation_direct') return { ...ALL_ASSET_CLASSES_SUPPORTED };
  if (conn.adapter_type !== 'connecttrade') return { ...NO_ASSET_CLASSES_SUPPORTED };
  const broker = findConnectTradeBroker(conn.broker_name);
  if (!broker) return { ...NO_ASSET_CLASSES_SUPPORTED };
  return {
    equities: broker.equities === true,
    options: broker.options === true,
    futures: broker.futures === true,
  };
}

/** Equities / options / futures flags for the selected account's broker. */
export function getBrokerAssetClassSupport(user, account = null) {
  if (!user) return { ...NO_ASSET_CLASSES_SUPPORTED };
  const conn = account
    ? resolveBrokerConnectionForAccount(user, account)
    : user.activeBrokerConnection || null;
  if (conn) return connectionAssetClassSupport(conn);
  if (isTradestationDirectBroker(user)) return { ...ALL_ASSET_CLASSES_SUPPORTED };
  // Unresolved / unlinked: do not block; market-data gates handle empty charts.
  return { ...ALL_ASSET_CLASSES_SUPPORTED };
}

/** Asset classes the effective MD source can chart. */
export function getMarketDataAssetClassSupport(user, account = null) {
  const md = resolveMarketDataConnection(user, account);
  if (md) return connectionAssetClassSupport(md);
  return getBrokerAssetClassSupport(user, account);
}

/**
 * True when the selected broker covers this asset class.
 * Unknown / crypto classes are allowed so we do not block charts while details load.
 */
export function brokerSupportsAssetClass(user, account, assetClass) {
  if (!assetClass || assetClass === 'crypto') return true;
  const support = getBrokerAssetClassSupport(user, account);
  if (!(assetClass in support)) return true;
  return support[assetClass] === true;
}

export function getUnsupportedAssetClassMessage(user, account, assetClass) {
  const conn =
    resolveMarketDataConnection(user, account) ||
    (account ? resolveBrokerConnectionForAccount(user, account) : user?.activeBrokerConnection) ||
    null;
  const broker = formatBrokerDisplayName(conn?.broker_name);
  const label =
    assetClass === 'futures' ? 'futures' : assetClass === 'options' ? 'options' : assetClass || 'this asset class';
  return `${broker} does not support ${label}. Choose a supported symbol, or connect a broker that includes ${label}.`;
}

/**
 * After ticker details (or futures pattern match), tell the UI to skip bars/quotes.
 * Charts follow the MD source connection's asset classes, not the selected account.
 */
export function getUnsupportedAssetClassBlock(user, account, ticker, tickerDetails = null) {
  if (!ticker || !user) return null;
  if (!supportsMarketData(user, account)) return null;
  const assetClass = getBrokerAssetClass(ticker, tickerDetails);
  if (!assetClass || assetClass === 'crypto') return null;
  const support = getMarketDataAssetClassSupport(user, account);
  if (!(assetClass in support) || support[assetClass] === true) return null;
  return {
    assetClass,
    message: getUnsupportedAssetClassMessage(user, account, assetClass),
  };
}

/** True when this account is allowed to trade single-leg options. */
export function accountSupportsOptions(account) {
  return account?.OptionsTradingEnabled === true;
}
